import React, { useCallback, useEffect, useRef, useState } from 'react';
import { prop } from 'ramda';
import { useIsMounted } from 'js/hooks/useIsMounted';
import { useComponentTranslations } from 'js/hooks/component';
import { Autocomplete } from 'js/components/common/Fields/Autocomplete';
import { IAutocompleteOption } from 'js/components/common/Fields/Autocomplete/types';

import * as Types from '../../types';
import * as hooks from '../../hooks';
import * as helpers from '../../helpers';
import { SearchOption } from '../SearchOption';
import { useDynamicReducerSelector } from '../../../../store';
import { useFieldLabel } from '../../../ChangeFormField/hooks';

export const HouseNumberLookup: React.FC<Types.IInnerLookupFieldConfig> = ({
    disabled,
    field,
    fieldId,
    isFieldHidden,
    className,
    classNames,
    retrievedAddress,
    onRetrieve,
    ChangeFormField
}) => {
    const isMounted = useIsMounted();
    const translate = useComponentTranslations();
    const searchHouseNumber = hooks.useSearchHouseNumber();
    const hiddenFields = useDynamicReducerSelector(prop('hiddenFields'));
    const searchText = useRef<string>('');
    const [error, setError] = useState('');

    const houseNumberField = field.innerFields.houseNumber;
    const showInnerField = houseNumberField && !hiddenFields.includes(houseNumberField.name);
    const showAddressLookup = !showInnerField && retrievedAddress?.streetName;

    const validate = hooks.useLookupValidate(houseNumberField);
    const [label, getLabelText] = useFieldLabel(houseNumberField?.label || '');

    const setAddressLookupError = useCallback((err: any) => {
        const defaultError = translate('playtech.mobile.system.form.addressLookupSwiss.serviceError.default');
        const message = translate(`playtech.mobile.system.form.addressLookupSwiss.serviceError.${err?.errorCode}`,
            err.errorMessage || defaultError
        );

        setError(message);
    }, [translate]);

    const onSearchChange = useCallback(async (value: string): Promise<IAutocompleteOption[] | void> => {
        setError('');
        searchText.current = value;

        try {
            const searchResults = await searchHouseNumber(value, retrievedAddress);

            return helpers.houseSearchResultsToOptions(searchResults, searchText.current);
        } catch (postCodeError: any) {
            isMounted() && setAddressLookupError(postCodeError);
        }
    }, [isMounted, retrievedAddress, searchHouseNumber, setAddressLookupError]);

    const onOptionSelect = useCallback(async (option: IAutocompleteOption): Promise<IAutocompleteOption[] | void> => {
        const selectedItem = option.data?.retrievedAddress as Types.IParsedAutoCompleteItem;

        setError('');
        searchText.current = '';

        onRetrieve(selectedItem);
    }, [onRetrieve]);

    useEffect(() => {
        if (!showAddressLookup) {
            setError('');
            searchText.current = '';
        }
    }, [showAddressLookup]);

    if (!houseNumberField) {
        return null;
    }

    const { isLabelAsteriskEnabled, isPlaceholderAsteriskEnabled } = houseNumberField.validationRules?.find(rule => rule.type === 'required') || {};
    const placeholder = houseNumberField.placeholder && translate(houseNumberField.placeholder, houseNumberField.placeholder);

    return (
        <>
            {showAddressLookup && (
                <Autocomplete
                    aria-label={getLabelText() || placeholder || translate('playtech.mobile.system.input.aria-label')}
                    className={className}
                    classNames={{ container: classNames?.autocomplete }}
                    disabled={disabled}
                    error={error}
                    id={fieldId}
                    infoTooltip={
                        houseNumberField.infoTooltip && translate(houseNumberField.infoTooltip, houseNumberField.infoTooltip)
                    }
                    isLabelAsteriskEnabled={isLabelAsteriskEnabled}
                    isPlaceholderAsteriskEnabled={isPlaceholderAsteriskEnabled}
                    label={label}
                    minSymbolsToSearch={1}
                    name={`${houseNumberField.name}_lookup`}
                    noOptionsMessage={translate('playtech.mobile.system.form.addressLookupSwiss.serviceError.noResults')}
                    options={[]}
                    Option={SearchOption}
                    onSearchChange={onSearchChange}
                    onOptionSelect={onOptionSelect}
                    placeholder={placeholder}
                    validate={validate}
                />
            )}
            {showInnerField && (
                <ChangeFormField
                    field={houseNumberField}
                    className={className}
                    isFieldHidden={isFieldHidden}
                    disabled={disabled || retrievedAddress?.houseNumber}
                />
            )}
        </>
    );
};
